import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

const ITEM_SELECTOR = ".fly-journeyNavB2C__item";
const CARD_SELECTION_EVENT = "flyCardSelection:change";
const CARD_SELECTION_STATE_EVENT = "flyCardSelection:state";
const CURRENT_CLASS = "fly-journeyNavB2C__item--current";
const PENDING_CLASS = "fly-journeyNavB2C__item--pending";
const SELECTED_CLASS = "fly-journeyNavB2C__item--selected";
const SELECTED_TEXT = ".fly-journeyNavB2C__selected-text";

/** Tras un scroll programático se ignoran los eventos de scroll que genera. */
const PROGRAMMATIC_SCROLL_MS = 800;
const SWIPE_SETTLE_MS = 150;
/** Pausa tras seleccionar una card antes de cambiar de paso. */
const AUTO_ADVANCE_DELAY_MS = 350;

/**
 * Navegación por pasos del trayecto (Ida / Vuelta).
 *
 * - Clic en un paso: lo marca como activo y desplaza en horizontal los
 *   contenedores indicados en `data-journey-nav-scroll` hasta la columna de
 *   ese paso (Ida = inicio, Vuelta = final).
 * - Deslizar uno de esos contenedores actualiza el paso activo.
 * - Al seleccionar una card pasa solo al paso siguiente (ida → vuelta); si la
 *   card es de la última columna (la vuelta) vuelve al primer paso (ida).
 *   Escucha el evento `flyCardSelection:change` del plugin
 *   `fly-card-selection`, así que no cambia de paso al deseleccionar.
 * - Marca como `--selected` los pasos con vuelo elegido según el evento
 *   `flyCardSelection:state` (`{ steps: boolean[] }`) que emite
 *   `fly-card-selection` en `document`.
 *
 * Emite `journeyNav:change` con `{ index }` cuando cambia el paso.
 */
export class FlyJourneyNav implements Plugin {
  private items: HTMLButtonElement[] = [];
  private scrollers: HTMLElement[] = [];
  private current = 0;
  private programmaticUntil = 0;
  private readonly settleTimers = new WeakMap<HTMLElement, number>();
  private readonly clickHandler = (event: MouseEvent) =>
    this.handleClick(event);
  private readonly scrollHandler = (event: Event) =>
    this.handleScroll(event.currentTarget as HTMLElement);
  private readonly cardSelectHandler = (event: Event) =>
    this.handleCardSelect(event as CustomEvent<{ selected: boolean }>);
  private readonly selectionStateHandler = (event: Event) =>
    this.setSelectedSteps(
      (event as CustomEvent<{ steps?: boolean[] }>).detail?.steps ?? [],
    );
  private autoAdvanceTimer: number | null = null;

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.items = Array.from(
      this.element.querySelectorAll<HTMLButtonElement>(ITEM_SELECTOR),
    );
    if (this.items.length === 0) return;

    const currentIndex = this.items.findIndex((item) =>
      item.classList.contains(CURRENT_CLASS),
    );
    this.current = Math.max(0, currentIndex);

    const selector = this.element.dataset.journeyNavScroll;
    this.scrollers = selector
      ? Array.from(document.querySelectorAll<HTMLElement>(selector))
      : [];

    this.element.addEventListener("click", this.clickHandler);
    document.addEventListener(
      CARD_SELECTION_STATE_EVENT,
      this.selectionStateHandler,
    );
    this.scrollers.forEach((scroller) => {
      scroller.addEventListener("scroll", this.scrollHandler, {
        passive: true,
      });
      scroller.addEventListener(CARD_SELECTION_EVENT, this.cardSelectHandler);
    });
  }

  destroy(): void {
    this.element.removeEventListener("click", this.clickHandler);
    document.removeEventListener(
      CARD_SELECTION_STATE_EVENT,
      this.selectionStateHandler,
    );
    this.scrollers.forEach((scroller) => {
      scroller.removeEventListener("scroll", this.scrollHandler);
      scroller.removeEventListener(
        CARD_SELECTION_EVENT,
        this.cardSelectHandler,
      );
      window.clearTimeout(this.settleTimers.get(scroller));
    });
    this.scrollers = [];
    if (this.autoAdvanceTimer !== null) {
      window.clearTimeout(this.autoAdvanceTimer);
      this.autoAdvanceTimer = null;
    }
  }

  goTo(index: number, source?: HTMLElement): void {
    if (index < 0 || index >= this.items.length) return;

    this.setCurrent(index);
    this.programmaticUntil = performance.now() + PROGRAMMATIC_SCROLL_MS;

    this.scrollers.forEach((scroller) => {
      if (scroller === source) return;
      const rect = scroller.getBoundingClientRect();
      const inView = rect.bottom > 0 && rect.top < window.innerHeight;
      scroller.scrollTo({
        left: this.getScrollLeftFor(scroller, index),
        behavior: inView ? "smooth" : "instant",
      });
    });
  }

  private handleClick(event: MouseEvent): void {
    const item = (event.target as HTMLElement).closest<HTMLButtonElement>(
      ITEM_SELECTOR,
    );
    if (!item) return;
    const index = this.items.indexOf(item);
    if (index !== -1) this.goTo(index);
  }

  private handleCardSelect(event: CustomEvent<{ selected: boolean }>): void {
    if (!event.detail?.selected) return;
    const card = event.target as HTMLElement;
    const scroller = event.currentTarget as HTMLElement;

    const column = this.getColumnIndex(scroller, card);
    // Ida → vuelta; desde la última columna se vuelve a la primera
    const target = (column + 1) % this.items.length;
    if (target === column) return;

    if (this.autoAdvanceTimer !== null) {
      window.clearTimeout(this.autoAdvanceTimer);
    }
    this.autoAdvanceTimer = window.setTimeout(() => {
      this.autoAdvanceTimer = null;
      this.goTo(target);
    }, AUTO_ADVANCE_DELAY_MS);
  }

  /** Columna (paso) de un elemento según su posición horizontal en el contenedor. */
  private getColumnIndex(scroller: HTMLElement, el: Element): number {
    // Se mide desde el centro: el borde izquierdo puede quedar unos px antes
    // de la mitad por paddings/gaps y caer en la columna anterior.
    const rect = el.getBoundingClientRect();
    const offsetCenter =
      rect.left +
      rect.width / 2 -
      scroller.getBoundingClientRect().left +
      scroller.scrollLeft;
    const columnWidth = scroller.scrollWidth / this.items.length;
    return Math.min(
      this.items.length - 1,
      Math.max(0, Math.floor(offsetCenter / columnWidth)),
    );
  }

  private handleScroll(scroller: HTMLElement): void {
    if (performance.now() < this.programmaticUntil) return;

    window.clearTimeout(this.settleTimers.get(scroller));
    this.settleTimers.set(
      scroller,
      window.setTimeout(() => {
        const index = this.getIndexForScroll(scroller);
        if (index !== this.current) this.goTo(index, scroller);
      }, SWIPE_SETTLE_MS),
    );
  }

  private getMaxScroll(scroller: HTMLElement): number {
    return Math.max(0, scroller.scrollWidth - scroller.clientWidth);
  }

  private getScrollLeftFor(scroller: HTMLElement, index: number): number {
    const steps = this.items.length - 1;
    return steps > 0 ? (this.getMaxScroll(scroller) * index) / steps : 0;
  }

  private getIndexForScroll(scroller: HTMLElement): number {
    const max = this.getMaxScroll(scroller);
    const steps = this.items.length - 1;
    if (max === 0 || steps === 0) return this.current;
    return Math.round((scroller.scrollLeft / max) * steps);
  }

  private setSelectedSteps(steps: boolean[]): void {
    this.items.forEach((item, i) => {
      const selected = Boolean(steps[i]);
      item.classList.toggle(SELECTED_CLASS, selected);
      const text = item.querySelector<HTMLElement>(SELECTED_TEXT);
      if (text) text.hidden = !selected;
    });
  }

  private setCurrent(index: number): void {
    const changed = index !== this.current;
    this.current = index;

    this.items.forEach((item, i) => {
      const isCurrent = i === index;
      item.classList.toggle(CURRENT_CLASS, isCurrent);
      item.classList.toggle(PENDING_CLASS, !isCurrent);
      if (isCurrent) {
        item.setAttribute("aria-current", "step");
      } else {
        item.removeAttribute("aria-current");
      }
    });

    if (changed) {
      this.element.dispatchEvent(
        new CustomEvent("journeyNav:change", {
          bubbles: true,
          detail: { index },
        }),
      );
    }
  }
}

PluginRegistry.register("fly-journey-nav", FlyJourneyNav);
